import { isAxiosError } from "axios";

type ErrorBag = Record<string, string[] | string | null | undefined>;

function toText(value: unknown): string | null {
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return null;
}

function collectValidationMessages(bag: ErrorBag | undefined): string[] {
  if (!bag) {
    return [];
  }

  return Object.entries(bag)
    .flatMap(([field, details]) => {
      const values = Array.isArray(details) ? details : [details];
      return values
        .map((item) => toText(item))
        .filter((item): item is string => Boolean(item))
        .map((item) => `${field}: ${item}`);
    });
}

export function getApiErrorMessage(error: unknown, fallbackAction: string): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const payload = error.response?.data as
      | {
          message?: unknown;
          title?: unknown;
          detail?: unknown;
          error?: unknown;
          errors?: ErrorBag;
          fieldErrors?: ErrorBag;
          validationErrors?: ErrorBag;
        }
      | undefined;

    const primary =
      toText(payload?.message)
      ?? toText(payload?.title)
      ?? toText(payload?.detail)
      ?? toText((payload?.error as { message?: unknown } | undefined)?.message)
      ?? toText(payload?.error);

    if (primary) {
      return primary;
    }

    const validationMessages = [
      ...collectValidationMessages(payload?.errors),
      ...collectValidationMessages(payload?.fieldErrors),
      ...collectValidationMessages(payload?.validationErrors)
    ];

    if (validationMessages.length > 0) {
      return validationMessages.join(" | ");
    }

    if (!error.response) {
      return `${fallbackAction}: unable to reach API server`;
    }

    if (status === 400) return `${fallbackAction}: request data is invalid`;
    if (status === 401) return `${fallbackAction}: session expired or unauthorized`;
    if (status === 403) return `${fallbackAction}: permission denied`;
    if (status === 404) return `${fallbackAction}: requested resource not found`;
    if (status === 409) return `${fallbackAction}: conflict with current system state`;
    if (status === 422) return `${fallbackAction}: validation failed`;
    if (status === 500) return `${fallbackAction}: server error`;

    return `${fallbackAction}: request failed${status ? ` (HTTP ${status})` : ""}`;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallbackAction;
}
